import axios from 'axios';
import analyticsService from './analyticsService';

// Configuración dinámica de la URL de la API
const API_URL = process.env.REACT_APP_API_URL || 
  (window.location.origin.includes('localhost') ? 'http://localhost:8080/api' : `${window.location.origin}/api`);

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 responses (expired/invalid token)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      console.warn('[PaymentService] Token expirado o inválido. Redirigiendo a login...');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      const currentPath = window.location.pathname + window.location.search;
      window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}&reason=session_expired`;
      return new Promise(function noop() { /* redirecting */ }); // Never resolve - page is redirecting
    }
    return Promise.reject(error);
  }
);

// Payment service
export const paymentService = {
  // Get available payment methods
  getPaymentMethods: async () => {
    try {
      const response = await api.get('/payments/methods');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get PSE banks from ePayco
  getPSEBanks: async () => {
    try {
      const response = await api.get('/payments/pse/banks');
      return response.data;
    } catch (error) {
      console.warn('Error getting PSE banks from API, using fallback:', error);
      // Fallback to static banks if API fails
      return {
        success: true,
        banks: [
          { id: '1077', name: 'BANKA', code: '1077', type: 'bank', enabled: true },
          { id: '1022', name: 'BANCO UNION COLOMBIANO', code: '1022', type: 'bank', enabled: true },
          { id: '1007', name: 'Bancolombia', code: '1007', type: 'bank', enabled: true },
          { id: '1019', name: 'Banco de Bogotá', code: '1019', type: 'bank', enabled: true },
          { id: '1023', name: 'Banco de Occidente', code: '1023', type: 'bank', enabled: true },
          { id: '1013', name: 'BBVA Colombia', code: '1013', type: 'bank', enabled: true },
          { id: '1032', name: 'Banco Popular', code: '1032', type: 'bank', enabled: true },
          { id: '1052', name: 'Banco AV Villas', code: '1052', type: 'bank', enabled: true },
          { id: '1051', name: 'Banco Davivienda', code: '1051', type: 'bank', enabled: true },
          { id: '1001', name: 'Banco Caja Social', code: '1001', type: 'bank', enabled: true },
          { id: '1551', name: 'Nequi', code: '1551', type: 'digital', enabled: true },
          { id: '1507', name: 'Daviplata', code: '1507', type: 'digital', enabled: true },
        ]
      };
    }
  },

  // Create PSE payment
  createPSEPayment: async (paymentData) => {
    try {
      // session_id se adjunta acá (no en el llamador) para que ninguna vía
      // de pago futura se olvide de la atribución de campaña — el backend
      // resuelve el UTM correspondiente por su cuenta (ver
      // resolveOrderAttribution en payment_controller.go).
      const response = await api.post('/payments/pse', {
        ...paymentData,
        session_id: analyticsService.getSessionId(),
        click_id: analyticsService.getClickId(),
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Create a new payment
  createPayment: async (paymentData) => {
    try {
      const response = await api.post('/payments', {
        ...paymentData,
        session_id: analyticsService.getSessionId(),
        click_id: analyticsService.getClickId(),
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get user payments
  getPayments: async () => {
    try {
      const response = await api.get('/payments');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get specific payment
  getPayment: async (paymentId) => {
    try {
      const response = await api.get(`/payments/${paymentId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Lookup payment by ref_epayco (public endpoint, no auth required)
  lookupPayment: async (refEpayco) => {
    try {
      const response = await api.get(`/payments/lookup?ref_epayco=${encodeURIComponent(refEpayco)}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Confirm payment
  confirmPayment: async (paymentId) => {
    try {
      const response = await api.post(`/payments/${paymentId}/confirm`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Format currency
  formatCurrency: (amount, currency = 'COP') => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  },

  // Get payment status color
  getStatusColor: (status) => {
    switch (status) {
      case 'approved':
        return 'green';
      case 'rejected':
        return 'red';
      case 'pending':
        return 'yellow';
      case 'failed':
        return 'red';
      default:
        return 'gray';
    }
  },

  // Get payment status label
  getStatusLabel: (status) => {
    switch (status) {
      case 'approved':
        return 'Aprobado';
      case 'rejected':
        return 'Rechazado';
      case 'pending':
        return 'Pendiente';
      case 'failed':
        return 'Fallido';
      default:
        return 'Desconocido';
    }
  },

  // Get payment method label
  getPaymentMethodLabel: (method) => {
    switch (method) {
      case 'credit_card':
        return 'Tarjeta de Crédito';
      case 'pse':
        return 'PSE';
      case 'cash':
        return 'Efectivo';
      case 'daviplata':
        return 'Daviplata';
      case 'safetypay':
        return 'SafetyPay';
      default:
        return method;
    }
  },

  // Validate credit card number (basic validation)
  validateCreditCard: (cardNumber) => {
    const cleaned = cardNumber.replace(/\D/g, '');
    if (cleaned.length < 13 || cleaned.length > 19) return false;
    
    // Luhn algorithm
    let sum = 0;
    let alternate = false;
    
    for (let i = cleaned.length - 1; i >= 0; i--) {
      let n = parseInt(cleaned.charAt(i), 10);
      
      if (alternate) {
        n *= 2;
        if (n > 9) n = (n % 10) + 1;
      }
      
      sum += n;
      alternate = !alternate;
    }
    
    return sum % 10 === 0;
  },

  // Format credit card number
  formatCreditCard: (cardNumber) => {
    const cleaned = cardNumber.replace(/\D/g, '');
    const match = cleaned.match(/(\d{1,4})(\d{1,4})?(\d{1,4})?(\d{1,4})?/);
    
    if (!match) return cardNumber;
    
    return [match[1], match[2], match[3], match[4]]
      .filter(Boolean)
      .join(' ');
  },

  // Get credit card type
  getCreditCardType: (cardNumber) => {
    const cleaned = cardNumber.replace(/\D/g, '');
    
    if (/^4/.test(cleaned)) return 'visa';
    if (/^5[1-5]/.test(cleaned)) return 'mastercard';
    if (/^3[47]/.test(cleaned)) return 'amex';
    if (/^6(?:011|5)/.test(cleaned)) return 'discover';
    
    return 'unknown';
  },

  // Banks for PSE (legacy - now using dynamic API)
  getPSEBanksLegacy: () => [
    { code: '1007', name: 'Bancolombia' },
    { code: '1019', name: 'Banco de Bogotá' },
    { code: '1023', name: 'Banco de Occidente' },
    { code: '1052', name: 'Banco AV Villas' },
    { code: '1032', name: 'Banco Popular' },
    { code: '1040', name: 'Banco Agrario' },
    { code: '1051', name: 'Banco Davivienda' },
    { code: '1001', name: 'Banco Caja Social' },
    { code: '1006', name: 'Banco Corpbanca' },
    { code: '1012', name: 'Banco GNB Sudameris' },
    { code: '1013', name: 'BBVA Colombia' },
    { code: '1292', name: 'Banco Falabella' },
    { code: '1060', name: 'Banco Pichincha' },
    { code: '1062', name: 'Banco Santander' },
    { code: '1551', name: 'Nequi' },
    { code: '1507', name: 'Daviplata' },
  ],

  // Document types for PSE
  getDocumentTypes: () => [
    { code: 'CC', name: 'Cédula de Ciudadanía' },
    { code: 'CE', name: 'Cédula de Extranjería' },
    { code: 'NIT', name: 'NIT' },
    { code: 'TI', name: 'Tarjeta de Identidad' },
    { code: 'PP', name: 'Pasaporte' },
    { code: 'RC', name: 'Registro Civil' },
  ],

  // Person types for PSE
  getPersonTypes: () => [
    { code: '0', name: 'Persona Natural' },
    { code: '1', name: 'Persona Jurídica' },
  ],
};

export default paymentService;
